import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { ADMIN_API_BASE } from "@/lib/adminApi";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider() {
  const [status, setStatus] = useState("checking");

  const checkSession = useCallback(async () => {
    try {
      const response = await fetch(`${ADMIN_API_BASE}/api/admin/session`, {
        credentials: "include",
        cache: "no-store",
      });
      setStatus(response.ok ? "authenticated" : "unauthenticated");
      return response.ok;
    } catch {
      setStatus("unauthenticated");
      return false;
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = useCallback(async (password) => {
    const response = await fetch(`${ADMIN_API_BASE}/api/admin/login`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });
    const body = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(body.detail || "Admin login failed.");
    setStatus("authenticated");
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch(`${ADMIN_API_BASE}/api/admin/logout`, {
        method: "POST",
        credentials: "include",
      });
    } finally {
      setStatus("unauthenticated");
    }
  }, []);

  const value = useMemo(() => ({ status, login, logout, checkSession }), [status, login, logout, checkSession]);
  return <AdminAuthContext.Provider value={value}><Outlet /></AdminAuthContext.Provider>;
}

export function RequireAdmin() {
  const { status } = useAdminAuth();
  const location = useLocation();
  if (status === "checking") {
    return <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", fontFamily: "'Nunito', sans-serif", color: "#6A4FC0" }}>Checking administrator session…</div>;
  }
  if (status !== "authenticated") {
    const returnTo = `${location.pathname}${location.search}`;
    return <Navigate to={`/admin/login?returnTo=${encodeURIComponent(returnTo)}`} replace />;
  }
  return <Outlet />;
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext);
  if (!context) throw new Error("useAdminAuth must be used inside AdminAuthProvider");
  return context;
}
